// src/components/kuestion/pages/ManageData.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useKuestionApi } from '../useKuestionApi';
import QuestionBuilder from './QuestionBuilder';
import { sanitizeJSON } from '../shared/bankJson';
import './ManageData.css';

const UploadIcon = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 14V3m-4 4l4-4 4 4"/><path d="M3 14v2a1 1 0 001 1h12a1 1 0 001-1v-2"/></svg>);
const TrashIcon = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 6h12M8 6V4h4v2m-7 0v10a1 1 0 001 1h6a1 1 0 001-1V6"/><line x1="9" y1="9" x2="9" y2="14"/><line x1="11" y1="9" x2="11" y2="14"/></svg>);
const SparkleIcon = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2l1.5 4.5L16 8l-4.5 1.5L10 14l-1.5-4.5L4 8l4.5-1.5z"/><path d="M15 13l.75 2.25L18 16l-2.25.75L15 19l-.75-2.25L12 16l2.25-.75z" opacity="0.5"/></svg>);
const FileIcon = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 2h7l4 4v11a1 1 0 01-1 1H5a1 1 0 01-1-1V3a1 1 0 011-1z"/><path d="M12 2v4h4"/></svg>);
const CheckIcon = () => (<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3.5 8.5 6.5 11.5 12.5 4.5"/></svg>);
const CopyIcon = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="6" width="10" height="10" rx="1.5"/><path d="M14 6V4.5A1.5 1.5 0 0012.5 3H4.5A1.5 1.5 0 003 4.5v8A1.5 1.5 0 004.5 14H6"/></svg>);
const PencilIcon = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 3.5a1.6 1.6 0 012.3 2.3L7.5 15.1l-3.1.8.8-3.1z"/><path d="M12.8 5.2l2 2"/></svg>);
const CloseIcon = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="5" y1="5" x2="15" y2="15"/><line x1="15" y1="5" x2="5" y2="15"/></svg>);
const AlertIcon = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2L1 18h18L10 2z"/><line x1="10" y1="8" x2="10" y2="12"/><circle cx="10" cy="15" r="0.5" fill="currentColor"/></svg>);
const SuccessIcon = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="10" r="8"/><polyline points="6.5 10 9 12.5 13.5 7.5"/></svg>);

// The client-side JSON check lives in shared/bankJson.js: the template editor's
// "Import the reply" runs the same one, so the two can never disagree.

const ManageData = () => {
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [showAiForm, setShowAiForm] = useState(false);
  const [copied, setCopied] = useState(false);
  const [validation, setValidation] = useState(null);
  const [uploading, setUploading] = useState(false);
  // `source` decides which of two very different jobs the prompt asks for:
  //   questions — the document already holds exam questions; transcribe them
  //   material  — the document is study text; author questions from it
  const [aiForm, setAiForm] = useState({
    examCode: '', module: '', type: 'multichoice',
    source: 'questions', reword: 'normal', difficulty: 'applied',
    totalQuestions: '', splitFiles: false, perFile: '100',
  });
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [showBuilder, setShowBuilder] = useState(false);
  // Bank whose parameters are being edited, plus the form backing the modal.
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ examCode: '', module: '', type: 'multichoice', reword: 'normal' });
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState('');

  // Identity comes from the bearer token; no user_id is sent anywhere.
  const api = useKuestionApi();

  // ── Load files from backend on mount ──────────────────────────────
  const loadFiles = useCallback(async () => {
    try {
      const data = await api.get(API_ENDPOINTS.KQ_FILE_LIST);
      setFiles((data || []).map(f => ({
        id: f.id, name: f.original_name, type: f.file_type,
        size: f.file_size, date: f.upload_date?.split('T')[0] || '',
        selected: false, validationOk: f.is_valid,
        // null when valid, an array of strings when invalid
        validationErrors: f.validation_msg,
        status: f.is_valid ? 'valid' : (f.file_type === 'json' ? 'invalid' : 'uploaded'),
        // Bank fields, joined server-side. All null when the upload produced no
        // bank (non-JSON, or invalid) — which is what disables Edit.
        bankId: f.bank_id ?? null,
        examCode: f.exam_code ?? null,
        module: f.module ?? null,
        questionType: f.question_type ?? null,
        rewordLevel: f.reword_level ?? null,
        questionCount: f.question_count ?? null,
      })));
    } catch (e) { console.error('Failed to load files:', e); }
  }, [api]);

  useEffect(() => { loadFiles(); }, [loadFiles]);

  // ── Upload to backend ─────────────────────────────────────────────
  const processFile = async (file) => {
    const ext = file.name.split('.').pop().toLowerCase();

    // Client-side JSON pre-check (instant feedback)
    if (ext === 'json') {
      try {
        const text = await file.text();
        const result = sanitizeJSON(text);
        if (!result.ok) {
          setValidation({ ok: false, errors: result.errors });
          return; // Don't upload invalid JSON
        }
        setValidation({ ok: true, summary: result.summary });
      } catch (e) {
        setValidation({ ok: false, errors: [`Could not read file: ${e.message}`] });
        return;
      }
    }

    // Upload to backend
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const d = await api.postForm(API_ENDPOINTS.KQ_FILE_UPLOAD, formData);

      if (d?.is_valid) {
        setValidation({ ok: true, summary: { examCode: d.name, totalQuestions: '', type: ext } });
      } else if (d?.validation_errors) {
        setValidation({ ok: false, errors: d.validation_errors });
      }
      // Refresh file list from backend
      await loadFiles();
    } catch (e) {
      // KuestionError carries copy already mapped from the reason code
      setValidation({ ok: false, errors: [e.message] });
    } finally {
      setUploading(false);
    }
  };

  const handleUpload = (e) => {
    Array.from(e.target.files || []).forEach(processFile);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    Array.from(e.dataTransfer.files || []).forEach(processFile);
  };

  // ── Delete ────────────────────────────────────────────────────────
  const deleteFile = async (fileId) => {
    try {
      await api.del(API_ENDPOINTS.KQ_FILE_DELETE(fileId));
      await loadFiles();
    } catch (e) {
      setValidation({ ok: false, errors: [e.message] });
    }
  };

  const deleteSelected = async () => {
    const ids = files.filter(f => f.selected).map(f => f.id);
    if (!ids.length) return;
    try {
      await api.postJson(API_ENDPOINTS.KQ_FILE_BULK_DELETE, { file_ids: ids });
      setValidation(null);
      await loadFiles();
    } catch (e) {
      setValidation({ ok: false, errors: [e.message] });
    }
  };

  // ── Edit bank parameters ──────────────────────────────────────────
  const openEdit = (file) => {
    setEditError('');
    setEditing({ bankId: file.bankId, fileName: file.name });
    setEditForm({
      examCode: file.examCode || '',
      module: file.module || '',
      type: file.questionType || 'multichoice',
      reword: file.rewordLevel || 'normal',
    });
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (!editForm.examCode.trim() || !editForm.module.trim()) {
      setEditError('Exam code and module are both required.');
      return;
    }
    setEditBusy(true);
    setEditError('');
    try {
      // The response carries the updated bank in the same shape as a
      // GET /banks row, so patch it in rather than re-fetching the list.
      const updated = await api.putJson(API_ENDPOINTS.KQ_BANK(editing.bankId), {
        exam_code: editForm.examCode.trim(),
        module: editForm.module.trim(),
        question_type: editForm.type,
        reword_level: editForm.reword,
      });
      // Patch every file row pointing at this bank rather than re-fetching.
      if (updated && updated.id != null) {
        setFiles(fs => fs.map(f => (f.bankId === updated.id ? {
          ...f,
          examCode: updated.exam_code,
          module: updated.module,
          questionType: updated.question_type,
          rewordLevel: updated.reword_level,
          questionCount: updated.question_count ?? f.questionCount,
        } : f)));
      } else {
        await loadFiles();
      }
      setEditing(null);
    } catch (e) {
      setEditError(e.message);
    } finally {
      setEditBusy(false);
    }
  };

  const toggleSelect = (id) => setFiles(p => p.map(f => f.id === id ? { ...f, selected: !f.selected } : f));
  const toggleAll = () => { const all = files.length > 0 && files.every(f => f.selected); setFiles(p => p.map(f => ({ ...f, selected: !all }))); };

  // ── AI Prompt ─────────────────────────────────────────────────────
  const generatePrompt = () => {
    const typeMap = { multichoice: 'Multiple Choice', draganddrop: 'Drag and Drop', mixed: 'Mixed (Multiple Choice + Drag and Drop)' };
    const rewordMap = { normal: 'Keep questions as-is without rewording', moderate: 'Moderately reword questions while preserving original meaning', hard: 'Heavily reword and rephrase questions to test deep understanding' };
    const difficultyMap = {
      recall: 'Recall — test whether the reader remembers key facts, definitions and terminology',
      applied: 'Applied — test whether the reader can apply a concept to a short realistic scenario',
      analysis: 'Analysis — test whether the reader can compare options, weigh trade-offs, and choose the best answer among several defensible ones',
    };

    const fromMaterial = aiForm.source === 'material';
    const qCount = aiForm.totalQuestions ? parseInt(aiForm.totalQuestions) : null;
    const qCountLine = qCount ? `\nNumber of Questions: ${qCount}` : '';
    const qCountRule = qCount
      ? `\n- Generate exactly ${qCount} questions total`
      : (fromMaterial
        ? '\n- Generate as many questions as the material genuinely supports without repeating a concept — roughly one per distinct idea'
        : '\n- Process ALL questions from the attached document');

    let splitRule = '';
    if (aiForm.splitFiles && qCount && parseInt(aiForm.perFile) > 0) {
      const perFile = parseInt(aiForm.perFile);
      const numFiles = Math.ceil(qCount / perFile);
      splitRule = `\n\nIMPORTANT — FILE SPLITTING:\nSplit the output into ${numFiles} separate JSON files, each containing up to ${perFile} questions.\n- File 1: questions 1–${Math.min(perFile, qCount)} (save as "${aiForm.examCode}_part1.json")\n${numFiles > 1 ? Array.from({length: numFiles - 1}, (_, i) => {
        const start = (i + 1) * perFile + 1;
        const end = Math.min((i + 2) * perFile, qCount);
        return `- File ${i + 2}: questions ${start}–${end} (save as "${aiForm.examCode}_part${i + 2}.json")`;
      }).join('\n') : ''}\nEach file must be a complete, standalone valid JSON with the same structure shown below. Each file must have its own "totalQuestions" reflecting that file's count.`;
    }

    // The two modes differ in premise, not just wording — one transcribes
    // existing questions, the other authors new ones from source material.
    const intro = fromMaterial
      ? `You are an exam question writer.

I am giving you STUDY MATERIAL — a book chapter, lecture notes or reference text. It does NOT contain ready-made exam questions. Read it, understand the concepts it teaches, and write original exam questions that test whether someone has genuinely understood it.`
      : `You are an exam question processor.

I am giving you a document that ALREADY CONTAINS exam questions with their answer options. Your job is to transcribe them into structured JSON — not to invent new ones.`;

    const levelLine = fromMaterial
      ? `Difficulty: ${difficultyMap[aiForm.difficulty]}`
      : `Rewording: ${rewordMap[aiForm.reword]}`;

    const rules = fromMaterial
      ? `${qCountRule}
- ${difficultyMap[aiForm.difficulty]}
- Cover the material broadly. Spread questions across its major topics in proportion to how much space each one gets — do not cluster them all on the opening pages.
- Every question must be answerable from the material alone. Never require outside knowledge.
- Write exactly one unambiguously correct option. The other options must be plausible to someone who only half-understood the material: draw them from common misconceptions, adjacent concepts, or near-miss definitions.
- Never use filler options such as "None of the above", "All of the above", or obviously wrong throwaways.
- Do not lift a sentence verbatim as the question stem if that sentence already contains the answer.
- Vary the phrasing. Do not open every question with the same construction.
- Do not ask about the document's own structure — no page numbers, figure numbers, or "according to Table 3".
- In "explanation", say why the correct option is right and name the concept or section it comes from.`
      : `${qCountRule}
- ${rewordMap[aiForm.reword]}
- Preserve all answer options accurately — do not add, remove or reorder them
- Keep the original correct answer
- Include explanations for each correct answer`;

    // rewordLevel only means something when there were source questions to
    // reword; difficulty only means something when we authored them.
    const provenance = fromMaterial
      ? `  "difficulty": "${aiForm.difficulty}",`
      : `  "rewordLevel": "${aiForm.reword}",`;

    // The output is uploaded to Kuestion as a file, not pasted — so ask for a
    // downloadable artefact and name it the way the uploader expects.
    const splitting = aiForm.splitFiles && qCount;
    const safeCode = (aiForm.examCode || 'questions').trim().replace(/\s+/g, '_');
    const fileNoun = splitting ? 'downloadable .json files' : 'a downloadable .json file';
    const fileNaming = splitting ? `"${safeCode}_part1.json", "${safeCode}_part2.json" and so on` : `"${safeCode}.json"`;

    setGeneratedPrompt(`${intro}

Exam Code: ${aiForm.examCode}
Module: ${aiForm.module}
Question Type: ${typeMap[aiForm.type]}
${levelLine}${qCountLine}${splitRule}

${aiForm.splitFiles && qCount ? 'Each output file' : 'Output'} must be ONLY valid JSON in this exact structure:

{
  "examCode": "${aiForm.examCode}",
  "module": "${aiForm.module}",
  "type": "${aiForm.type}",
${provenance}
  "totalQuestions": <number>,
  "questions": [
    {
      "id": 1,
      "question": "<question text>",
      "type": "${aiForm.type === 'mixed' ? 'multichoice or draganddrop' : aiForm.type}",
      "options": ["A. ...", "B. ...", "C. ...", "D. ..."],
      "correctAnswer": "<letter(s)>",
      "explanation": "<brief explanation>"
    }
  ]
}

Rules:
${rules}
- "totalQuestions" must equal the number of entries in "questions"
- Output ONLY valid JSON, no markdown, no backticks, no preamble

Delivery:
- Give me the result as ${fileNoun}, encoded UTF-8, named ${fileNaming}.
- I will upload ${aiForm.splitFiles && qCount ? 'these files' : 'this file'} straight into Kuestion, which validates ${aiForm.splitFiles && qCount ? 'each one' : 'it'} on arrival — so ${aiForm.splitFiles && qCount ? 'every file' : 'the file'} must parse as JSON on its own, with nothing before or after the closing brace.
- If you cannot attach a file, print the raw JSON as your entire reply so I can save it myself.`);
  };

  const copyPrompt = () => { navigator.clipboard.writeText(generatedPrompt); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const selectedCount = files.filter(f => f.selected).length;
  const canGenerate = aiForm.examCode.trim() && aiForm.module.trim();

  return (
    <div className="md-page" style={{ animation: 'kqFadeUp 0.5s ease both' }}>
      <div className="md-page-header">
        <div className="md-page-icon"><svg viewBox="0 0 42 42" fill="none"><ellipse cx="21" cy="12" rx="13" ry="5" stroke="#C0522E" strokeWidth="1.5" fill="rgba(247,135,100,0.12)"/><path d="M8 12v8c0 2.76 5.82 5 13 5s13-2.24 13-5v-8" stroke="#C0522E" strokeWidth="1.5"/><path d="M8 20v8c0 2.76 5.82 5 13 5s13-2.24 13-5v-8" stroke="rgba(192,82,46,0.45)" strokeWidth="1.5"/><path d="M21 28v-10m-3.5 3.5L21 18l3.5 3.5" stroke="#DA4167" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
        <div><h1 className="md-page-title">Manage Data</h1><p className="md-page-sub">Upload JSON question files, generate AI prompts, and manage your data sources.</p></div>
      </div>

      {validation && (
        <div className={`md-toast ${validation.ok ? 'md-toast-success' : 'md-toast-error'}`}>
          <div className="md-toast-header">
            {validation.ok ? <SuccessIcon /> : <AlertIcon />}
            <span>{validation.ok ? 'File validated successfully!' : 'Validation errors found'}</span>
            <button className="md-toast-close" onClick={() => setValidation(null)}><CloseIcon /></button>
          </div>
          {validation.ok && validation.summary && (
            <div className="md-toast-summary">
              <span><strong>{validation.summary.examCode}</strong> — {validation.summary.module || ''}</span>
              <span>{validation.summary.totalQuestions} questions · {validation.summary.type}</span>
            </div>
          )}
          {!validation.ok && validation.errors && (
            <div className="md-toast-errors">{validation.errors.map((e, i) => <div key={i} className="md-toast-err-item">• {e}</div>)}</div>
          )}
        </div>
      )}

      <div className="md-layout">
        <div className="md-actions-panel">
          <div className="md-section-label">Actions</div>
          <div className={`md-upload-zone ${dragOver ? 'md-drag-over' : ''} ${uploading ? 'md-uploading' : ''}`} onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop} onClick={() => !uploading && fileInputRef.current?.click()}>
            <div className="md-upload-icon"><UploadIcon /></div>
            <span className="md-upload-label">{uploading ? 'Uploading…' : 'Upload File'}</span>
            <span className="md-upload-hint">JSON files auto-validated</span>
            <input ref={fileInputRef} type="file" multiple hidden onChange={handleUpload} accept=".csv,.xlsx,.xls,.pdf,.json,.txt" />
          </div>
          <button className={`md-action-btn md-btn-delete ${selectedCount === 0 ? 'md-btn-disabled' : ''}`} onClick={deleteSelected} disabled={selectedCount === 0}><TrashIcon /><span>Delete Selected</span>{selectedCount > 0 && <span className="md-badge-count">{selectedCount}</span>}</button>
          <button className="md-action-btn md-btn-ai" onClick={() => { setShowAiForm(true); setGeneratedPrompt(''); }}><SparkleIcon /><span>AI Prompt Generator</span></button>
          <button className="md-action-btn md-btn-manual" onClick={() => setShowBuilder(true)}><PencilIcon /><span>Create Manually</span></button>
        </div>

        <div className="md-table-panel">
          <div className="md-table-header"><span className="md-table-title">Uploaded Files</span><span className="md-table-count">{files.length} file{files.length !== 1 ? 's' : ''}</span></div>
          <div className="md-table-wrap">
            <table className="md-table">
              <thead><tr>
                <th className="md-th-select"><button className={`md-checkbox ${files.length > 0 && files.every(f => f.selected) ? 'md-checked' : ''}`} onClick={toggleAll}>{files.length > 0 && files.every(f => f.selected) && <CheckIcon />}</button></th>
                <th>File</th><th>Exam Code</th><th>Module</th><th>Status</th><th>Upload Date</th><th className="md-th-actions"></th>
              </tr></thead>
              <tbody>
                {files.length === 0 ? (
                  <tr><td colSpan="7" className="md-empty-row"><div className="md-empty-icon"><FileIcon /></div>No files uploaded yet. Use AI Prompt Generator to create JSON files, then upload them here.</td></tr>
                ) : files.map(file => (
                  <tr key={file.id} className={file.selected ? 'md-row-selected' : ''}>
                    <td className="md-td-select"><button className={`md-checkbox ${file.selected ? 'md-checked' : ''}`} onClick={() => toggleSelect(file.id)}>{file.selected && <CheckIcon />}</button></td>
                    <td><div className="md-file-cell"><div className="md-file-icon"><FileIcon /></div><span className="md-file-name">{file.name}</span></div></td>
                    <td>{file.examCode ? <span className="md-code-badge">{file.examCode}</span> : <span className="md-cell-empty">—</span>}</td>
                    <td className="md-cell-module">{file.module || <span className="md-cell-empty">—</span>}</td>
                    <td>
                      {file.type === 'json' ? (
                        <span className={`md-status-badge ${file.validationOk ? 'md-status-valid' : 'md-status-invalid'}`}>{file.validationOk ? '✓ Valid' : '✗ Invalid'}</span>
                      ) : (
                        <span className="md-status-badge md-status-other">Uploaded</span>
                      )}
                    </td>
                    <td className="md-date-cell">{file.date}</td>
                    <td className="md-td-actions">
                      <div className="md-row-actions">
                        <button
                          className="md-row-edit"
                          onClick={() => file.bankId != null && openEdit(file)}
                          disabled={file.bankId == null}
                          title={file.bankId != null ? 'Edit exam code, module and type' : 'No question bank came from this file, so there are no parameters to edit'}
                        >
                          <PencilIcon />
                        </button>
                        <button className="md-row-delete" onClick={() => deleteFile(file.id)} title="Delete file"><TrashIcon /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {showBuilder && (
        <QuestionBuilder
          onClose={() => setShowBuilder(false)}
          onUpload={async (file) => { await processFile(file); }}
        />
      )}

      {/* Edit the parameters the upload derived — these live on the question
          bank, which is what the template screen reads and filters on. */}
      {editing && ReactDOM.createPortal(
        <div className="md-modal-overlay" onClick={() => !editBusy && setEditing(null)}>
          <div className="md-modal md-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="md-modal-header">
              <div className="md-modal-title-row"><PencilIcon /><span>Edit parameters</span></div>
              <button className="md-modal-close" onClick={() => setEditing(null)} disabled={editBusy}><CloseIcon /></button>
            </div>
            <div className="md-modal-body">
              <p className="md-modal-desc">
                Changing these updates the question bank built from <strong>{editing.fileName}</strong>.
                Exam templates already using it will pick up the new values. The uploaded file itself is not rewritten.
              </p>

              {editError && <div className="md-inline-error">{editError}</div>}

              <div className="md-form-grid">
                <div className="md-form-group">
                  <label className="md-form-label">Exam Code <span className="md-req">*</span></label>
                  <input className="md-form-input" type="text" value={editForm.examCode} disabled={editBusy}
                    onChange={e => setEditForm(s => ({ ...s, examCode: e.target.value }))} />
                </div>
                <div className="md-form-group">
                  <label className="md-form-label">Module <span className="md-req">*</span></label>
                  <input className="md-form-input" type="text" value={editForm.module} disabled={editBusy}
                    onChange={e => setEditForm(s => ({ ...s, module: e.target.value }))} />
                </div>
              </div>

              <div className="md-form-group"><label className="md-form-label">Question Type</label>
                <div className="md-radio-group">{[['multichoice','Multiple Choice'],['draganddrop','Drag & Drop'],['mixed','Mixed']].map(([v,l]) => (
                  <label key={v} className={`md-radio-card ${editForm.type === v ? 'md-radio-active' : ''}`}>
                    <input type="radio" name="edit-qtype" value={v} checked={editForm.type === v} onChange={e => setEditForm(s => ({ ...s, type: e.target.value }))} />
                    <span className="md-radio-dot" /><span>{l}</span>
                  </label>))}
                </div>
              </div>

              <div className="md-form-group"><label className="md-form-label">Reword Complexity</label>
                <div className="md-radio-group">{[['normal','Normal (As-is)'],['moderate','Moderate'],['hard','Hard']].map(([v,l]) => (
                  <label key={v} className={`md-radio-card md-radio-reword ${editForm.reword === v ? 'md-radio-active' : ''}`}>
                    <input type="radio" name="edit-reword" value={v} checked={editForm.reword === v} onChange={e => setEditForm(s => ({ ...s, reword: e.target.value }))} />
                    <span className="md-radio-dot" /><span>{l}</span>
                  </label>))}
                </div>
              </div>

              <p className="md-form-note">
                These describe the bank. They do not change the questions themselves —
                to alter those, re-generate the file and upload it again.
              </p>

              <div className="md-modal-actions">
                <button className="md-btn-ghost" onClick={() => setEditing(null)} disabled={editBusy}>Cancel</button>
                <button className="md-generate-btn md-btn-inline" onClick={saveEdit} disabled={editBusy}>
                  {editBusy ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Portalled to <body>. .md-page carries `animation: kqFadeUp ... both`,
          which leaves a transform on the element — and a transformed ancestor
          becomes the containing block for position:fixed children, so an
          in-place overlay anchors to the page column instead of the viewport
          and its header scrolls out of reach. */}
      {showAiForm && ReactDOM.createPortal(
        <div className="md-modal-overlay" onClick={() => setShowAiForm(false)}>
          <div className="md-modal" onClick={e => e.stopPropagation()}>
            <div className="md-modal-header"><div className="md-modal-title-row"><SparkleIcon /><span>AI Prompt Generator</span></div><button className="md-modal-close" onClick={() => setShowAiForm(false)}><CloseIcon /></button></div>
            <div className="md-modal-body">
              <p className="md-modal-desc">Configure exam parameters. The generated prompt can be pasted into ChatGPT, Gemini, Copilot, or Claude alongside your document — a question bank to transcribe, or a chapter to write questions from — to produce a structured JSON file.</p>
              <div className="md-form-grid">
                <div className="md-form-group"><label className="md-form-label">Exam Code <span className="md-req">*</span></label><input className="md-form-input" type="text" placeholder="e.g. SAA-C03" value={aiForm.examCode} onChange={e => setAiForm(s => ({ ...s, examCode: e.target.value }))} /></div>
                <div className="md-form-group"><label className="md-form-label">Module <span className="md-req">*</span></label><input className="md-form-input" type="text" placeholder="e.g. AWS Solutions Architect" value={aiForm.module} onChange={e => setAiForm(s => ({ ...s, module: e.target.value }))} /></div>
              </div>
              <div className="md-form-group"><label className="md-form-label">What is in your document?</label>
                <div className="md-radio-group">
                  {[['questions','Exam questions','It already has questions and options — transcribe them'],
                    ['material','Study material','A chapter or notes — write new questions from it']].map(([v,l,hint]) => (
                    <label key={v} className={`md-radio-card md-radio-source ${aiForm.source === v ? 'md-radio-active' : ''}`}>
                      <input type="radio" name="source" value={v} checked={aiForm.source === v} onChange={e => setAiForm(s => ({ ...s, source: e.target.value }))} />
                      <span className="md-radio-dot" />
                      <span className="md-radio-stack"><span>{l}</span><span className="md-radio-hint">{hint}</span></span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="md-form-group"><label className="md-form-label">Question Type</label>
                <div className="md-radio-group">{[['multichoice','Multiple Choice'],['draganddrop','Drag & Drop'],['mixed','Mixed']].map(([v,l]) => (<label key={v} className={`md-radio-card ${aiForm.type === v ? 'md-radio-active' : ''}`}><input type="radio" name="qtype" value={v} checked={aiForm.type === v} onChange={e => setAiForm(s => ({ ...s, type: e.target.value }))} /><span className="md-radio-dot" /><span>{l}</span></label>))}</div>
              </div>
              {aiForm.source === 'material' ? (
                <div className="md-form-group"><label className="md-form-label">Difficulty</label>
                  <div className="md-radio-group">{[['recall','Recall'],['applied','Applied'],['analysis','Analysis']].map(([v,l]) => (<label key={v} className={`md-radio-card md-radio-reword ${aiForm.difficulty === v ? 'md-radio-active' : ''}`}><input type="radio" name="difficulty" value={v} checked={aiForm.difficulty === v} onChange={e => setAiForm(s => ({ ...s, difficulty: e.target.value }))} /><span className="md-radio-dot" /><span>{l}</span></label>))}</div>
                </div>
              ) : (
                <div className="md-form-group"><label className="md-form-label">Reword Complexity</label>
                  <div className="md-radio-group">{[['normal','Normal (As-is)'],['moderate','Moderate'],['hard','Hard']].map(([v,l]) => (<label key={v} className={`md-radio-card md-radio-reword ${aiForm.reword === v ? 'md-radio-active' : ''}`}><input type="radio" name="reword" value={v} checked={aiForm.reword === v} onChange={e => setAiForm(s => ({ ...s, reword: e.target.value }))} /><span className="md-radio-dot" /><span>{l}</span></label>))}</div>
                </div>
              )}
              <div className="md-form-grid">
                <div className="md-form-group"><label className="md-form-label">Total Questions</label><input className="md-form-input" type="number" placeholder={aiForm.source === 'material' ? 'e.g. 20 (blank = as many as the text supports)' : 'e.g. 300 (blank = all in the document)'} min="1" value={aiForm.totalQuestions} onChange={e => setAiForm(s => ({ ...s, totalQuestions: e.target.value }))} /></div>
                <div className="md-form-group"><label className="md-form-label">Split into Multiple Files</label>
                  <div className="md-split-row">
                    <button className={`md-split-toggle ${aiForm.splitFiles ? 'md-split-on' : ''}`} onClick={() => setAiForm(s => ({ ...s, splitFiles: !s.splitFiles }))}><span className="md-split-knob" /></button>
                    {aiForm.splitFiles && (
                      <div className="md-split-config">
                        <input className="md-form-input md-split-input" type="number" min="10" placeholder="100" value={aiForm.perFile} onChange={e => setAiForm(s => ({ ...s, perFile: e.target.value }))} />
                        <span className="md-split-label">questions per file</span>
                      </div>
                    )}
                  </div>
                  {aiForm.splitFiles && aiForm.totalQuestions && parseInt(aiForm.perFile) > 0 && (
                    <div className="md-split-preview">Will generate {Math.ceil(parseInt(aiForm.totalQuestions) / parseInt(aiForm.perFile))} file{Math.ceil(parseInt(aiForm.totalQuestions) / parseInt(aiForm.perFile)) > 1 ? 's' : ''} ({aiForm.perFile} questions each{parseInt(aiForm.totalQuestions) % parseInt(aiForm.perFile) !== 0 ? `, last file: ${parseInt(aiForm.totalQuestions) % parseInt(aiForm.perFile)}` : ''})</div>
                  )}
                </div>
              </div>
              <button className="md-generate-btn" onClick={generatePrompt} disabled={!canGenerate}><SparkleIcon /> Generate Prompt</button>
              {generatedPrompt && (
                <div className="md-prompt-output">
                  <div className="md-prompt-header"><span>Generated Prompt</span><button className="md-copy-btn" onClick={copyPrompt}><CopyIcon /> {copied ? 'Copied!' : 'Copy'}</button></div>
                  <pre className="md-prompt-text">{generatedPrompt}</pre>
                  <div className="md-prompt-instructions"><strong>Next Steps:</strong> Copy → Paste into ChatGPT / Gemini / Copilot / Claude with your document → Download the JSON file → Upload it here (auto-validated).</div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default ManageData;
